import React, { useEffect, useState } from "react";
import type { ChangePasswordModel } from "../../type/password";
import type { UserModel } from "../../type/user";

import { fetchWithAuth } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";
import { useToast } from "../../components/toast/toast";

import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalBody } from "../../components/modalCommon/modalBody";
import { ModalFooter } from "../../components/modalCommon/modalFooter";

import "./changePassword.scss";

const ChangePasswordModal: React.FC<ChangePasswordModel> = ({
  show,
  onClose,
  iduser,
  isAdmin = false,
}) => {
  const [userInfo, setUserInfo] = useState<UserModel | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { showToast } = useToast();

  useEffect(() => {
    if (!show || !iduser) return;

    fetchWithAuth(`/api/user/get/${iduser}`).then(async (resp) => {
      if (resp.ok) {
        const data = await resp.json();
        setUserInfo(data);
      }
    });
  }, [show, iduser]);

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setFormError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const validateForm = () => {
    if ((!isAdmin && !currentPassword) || !newPassword || !confirmPassword) {
      setFormError("Please fill in all fields.");
      return false;
    }

    if (newPassword.length < 8) {
      setFormError("New password must be at least 8 characters.");
      return false;
    }

    if (newPassword !== confirmPassword) {
      setFormError("New password and confirmation do not match.");
      return false;
    }

    setFormError(null);
    return true;
  };

  const handleSubmitChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const apiPath = isAdmin
        ? "auth/change-password-admin"
        : "auth/change-password";

      const requestBody: {
        iduser: number;
        oldPassword?: string;
        newPassword: string;
      } = {
        iduser,
        newPassword,
      };

      if (!isAdmin) {
        requestBody.oldPassword = currentPassword;
      }

      const resp = await fetchWithAuth(getApiUrl(apiPath), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || "Failed to change password.");
      }

      const data = await resp.json();
      showToast(data.message || "Password updated successfully!", "info");

      resetForm();
      onClose();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setFormError(err.message || "Error changing password.");
      } else {
        setFormError("Error changing password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal open={show} onClose={handleClose} size="sm">
      <ModalHeader title="Change Password" onClose={handleClose} />

      <form onSubmit={handleSubmitChangePassword}>
        <ModalBody>
          {userInfo && (
            <div className="change-password mb-3">
              <div>
                <strong>Username:</strong> {userInfo.username}
              </div>
              <div>
                <strong>Email:</strong> {userInfo.email}
              </div>
            </div>
          )}

          {formError && (
            <div className="alert alert-danger">{formError}</div>
          )}

          {!isAdmin && (
            <div className="mb-3">
              <label className="form-label">Current password</label>
              <input
                type="password"
                className="form-control bg-dark text-light border-secondary"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                autoFocus
                disabled={submitting}
              />
            </div>
          )}

          <div className="mb-3">
            <label className="form-label">New password</label>
            <input
              type="password"
              className="form-control bg-dark text-light border-secondary"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min. 8 characters"
              disabled={submitting}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Confirm new password</label>
            <input
              type="password"
              className="form-control bg-dark text-light border-secondary"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              disabled={submitting}
            />
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Save Password"}
          </button>
        </ModalFooter>
      </form>
    </BaseModal>
  );
};

export default ChangePasswordModal;
